import { getColorSchemeById } from '../theme/colorSchemes';

export interface BackgroundTemplate {
  id: string;
  name: string;
  type: 'gradient' | 'image' | 'animation';
  css: string;
  animation?: string;
  description: string;
}

const hexToRgb = (hex: string) => {
  const h = String(hex || '').replace('#', '').trim();
  if (h.length === 3) {
    const r = parseInt(h[0] + h[0], 16);
    const g = parseInt(h[1] + h[1], 16);
    const b = parseInt(h[2] + h[2], 16);
    return { r, g, b };
  }
  if (h.length === 6) {
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return { r, g, b };
  }
  return { r: 102, g: 126, b: 234 };
};

const buildSiteDefaultTemplate = (): BackgroundTemplate => {
  const ws: any = typeof window !== 'undefined' ? (window as any).__websiteSettings : null;
  const platformName = String(ws?.branding?.platformName || ws?.platformName || ws?.name || '').trim() || 'Default';
  const scheme = getColorSchemeById(ws?.colorScheme);
  const primaryHex = scheme?.primary || '#667eea';
  const secondaryHex = scheme?.secondary || '#764ba2';

  const p = hexToRgb(primaryHex);
  const s = hexToRgb(secondaryHex);

  const css = `radial-gradient(1200px 600px at 12% 12%, rgba(${p.r},${p.g},${p.b},0.34), transparent 62%), radial-gradient(900px 600px at 92% 8%, rgba(${s.r},${s.g},${s.b},0.28), transparent 56%), ${scheme?.gradient || `linear-gradient(135deg, rgba(${p.r},${p.g},${p.b},0.92) 0%, rgba(${s.r},${s.g},${s.b},0.92) 100%)`}`;

  return {
    id: 'site-default',
    name: platformName,
    type: 'gradient',
    css,
    description: 'Matches your website colors',
  };
};

export const backgroundTemplates: Record<string, BackgroundTemplate> = {
  'site-default': {
    id: 'site-default',
    name: 'Default',
    type: 'gradient',
    css: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    description: 'Matches your website colors'
  },
  'purple-blue-gradient': {
    id: 'purple-blue-gradient',
    name: 'Purple Blue',
    type: 'gradient',
    css: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    description: 'Classic purple to blue gradient'
  },
  'sunset-gradient': {
    id: 'sunset-gradient',
    name: 'Sunset',
    type: 'gradient',
    css: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 50%, #fecfef 100%)',
    description: 'Warm sunset colors'
  },
  'ocean-gradient': {
    id: 'ocean-gradient',
    name: 'Ocean',
    type: 'gradient',
    css: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
    description: 'Deep ocean blues'
  },
  'forest-gradient': {
    id: 'forest-gradient',
    name: 'Forest',
    type: 'gradient',
    css: 'linear-gradient(135deg, #56ab2f 0%, #a8e6cf 100%)',
    description: 'Natural forest greens'
  },
  'dark-gradient': {
    id: 'dark-gradient',
    name: 'Dark Mode',
    type: 'gradient',
    css: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
    description: 'Dark and mysterious'
  },
  'light-gradient': {
    id: 'light-gradient',
    name: 'Light Mode',
    type: 'gradient',
    css: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
    description: 'Clean and bright'
  },
  'aurora-gradient': {
    id: 'aurora-gradient',
    name: 'Aurora',
    type: 'animation',
    css: 'linear-gradient(45deg, #667eea, #764ba2, #f093fb, #f5576c)',
    animation: 'aurora 8s ease-in-out infinite',
    description: 'Animated aurora effect'
  },
};

export class ThemeService {
  private static readonly THEME_STORAGE_KEY = 'dashboard_theme';
  private static readonly DEFAULT_THEME = 'site-default';

  static getCurrentTheme(): string {
    if (typeof window === 'undefined') return this.DEFAULT_THEME;
    
    const stored = localStorage.getItem(this.THEME_STORAGE_KEY);
    return stored || this.DEFAULT_THEME;
  }

  static setTheme(themeId: string): void {
    if (typeof window === 'undefined') return;
    
    localStorage.setItem(this.THEME_STORAGE_KEY, themeId);
    this.applyTheme(themeId);
  }

  static applyTheme(themeId: string): void {
    if (typeof window === 'undefined') return;

    const template = themeId === 'site-default' ? buildSiteDefaultTemplate() : backgroundTemplates[themeId];
    if (!template) return;

    const root = document.documentElement;
    
    // Apply background
    root.style.setProperty('--dashboard-background', template.css);
    
    // Apply animation if exists
    if (template.animation) {
      root.style.setProperty('--dashboard-animation', template.animation);
    } else {
      root.style.removeProperty('--dashboard-animation');
    }

    // Add animation keyframes for themes with overlay effects
    const overlayThemes = [
      'aurora-gradient'
    ];
    
    // Always add keyframes for themes with overlay effects
    if (overlayThemes.includes(themeId)) {
      this.addAuroraKeyframes();
    } else {
      this.removeAuroraKeyframes();
    }
  }

  private static addAuroraKeyframes(): void {
    if (typeof window === 'undefined') return;
    
    const styleId = 'aurora-keyframes';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes aurora {
        0%, 100% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
      }
      
      
      
      .dashboard-background {
        background-size: 400% 400%;
        animation: var(--dashboard-animation, none);
      }
      
      
      
    `;
    document.head.appendChild(style);
  }

  private static removeAuroraKeyframes(): void {
    if (typeof window === 'undefined') return;
    
    const style = document.getElementById('aurora-keyframes');
    if (style) {
      style.remove();
    }
  }

  static getAvailableThemes(): BackgroundTemplate[] {
    const base = Object.values(backgroundTemplates).filter(t => t.id !== 'site-default');
    return [buildSiteDefaultTemplate(), ...base];
  }

  static getThemeById(themeId: string): BackgroundTemplate | undefined {
    if (themeId === 'site-default') return buildSiteDefaultTemplate();
    return backgroundTemplates[themeId];
  }
}
